import type { MatchVm } from "../tournamentPage/matchVm";

export type CompactTeam = { code: string; score?: string; overs?: string; batting?: boolean; winner?: boolean };
export type Compact = {
  state: "live" | "upcoming" | "result";
  no: number;
  /** The one line that matters right now, already short: "NYX need 48 off 34", "Today, 7:00 PM CT", "NYX won by 4 wkts". */
  label: string;
  teams: [CompactTeam, CompactTeam];
  /** The side shown big: the one batting, else the winner, else the first. */
  lead: CompactTeam;
  other: CompactTeam;
};

/** Scores arrive as "24/0 & 10/0 & 13/0" for multi-innings matches. Live shows the innings in play (last), otherwise the first. */
const pickScore = (score: string | undefined, live: boolean) => {
  const parts = (score ?? "").split("&").map((x) => x.trim()).filter(Boolean);
  if (!parts.length) return { score: undefined, single: true };
  return { score: live ? parts[parts.length - 1] : parts[0], single: parts.length === 1 };
};

/** "19.1" stays, a finished "20.0" becomes "20"; the "/max" part is dropped. */
const overs = (o?: string) => (o ?? "").split("/")[0].replace(/\.0$/, "") || undefined;

/** Shorten backend wording for a ~150 px line: "wickets" -> "wkts", "45 runs required from 30 balls" -> "need 45 off 30", team name -> team code. */
export const shortenLabel = (text: string, teams: { name: string; code: string }[]) => {
  let s = text.trim().replace(/\s+/g, " ");
  s = s.replace(/\bwickets?\b/gi, "wkts");
  s = s.replace(/(\d+)\s+runs?\s+(?:required|needed)\s+(?:from|off|in)\s+(\d+)\s+balls?/i, "need $1 off $2");
  s = s.replace(/\bneeds?\s+(\d+)\s+runs?\s+(?:from|off|in)\s+(\d+)\s+balls?/i, "need $1 off $2");
  for (const t of teams) {
    const name = t.name.trim();
    if (name && s.toLowerCase().startsWith(name.toLowerCase()) && /^\W|^$/.test(s.slice(name.length, name.length + 1) || " ")) return `${t.code}${s.slice(name.length)}`;
  }
  return s;
};

/** "Today, 7:00 PM CT", "Tomorrow, 9:30 AM CT", "Thu, Nov 5, 7:00 PM CT"; "Starting soon" once the start time has passed. */
export const upcomingLabel = (start: Date | null, now: number) => {
  if (!start) return "Time to be announced";
  if (start.getTime() <= now) return "Starting soon";
  const dayOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((dayOf(start) - dayOf(new Date(now))) / 86_400_000);
  const day = days === 0 ? "Today" : days === 1 ? "Tomorrow" : start.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  const time = start.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZoneName: "short" });
  return `${day}, ${time}`;
};

/** `main` is the long status line the big card uses (equation, toss or result text). */
export const buildCompact = (vm: MatchVm, now: number, main: string): Compact => {
  const live = vm.bucket === "live";
  const names = [vm.a, vm.b].map((t) => ({ name: t.name, code: t.code }));
  const mk = (t: MatchVm["a"]): CompactTeam => {
    const sc = pickScore(t.score, live);
    return { code: t.code, score: sc.score, overs: sc.single ? overs(t.overs) : undefined, batting: !!t.batting, winner: !!t.won };
  };
  const teams: [CompactTeam, CompactTeam] = [mk(vm.a), mk(vm.b)];
  const lead = teams.find((t) => t.batting) ?? teams.find((t) => t.winner) ?? teams[0];
  const other = teams.find((t) => t !== lead) ?? teams[1];
  const state = vm.bucket === "up" ? "upcoming" : vm.bucket === "live" ? "live" : "result";
  const label = state === "upcoming" ? upcomingLabel(vm.start, now) : shortenLabel(main, names) || (state === "live" ? "Match in progress" : "Result");
  return { state, no: vm.no, label, teams, lead, other };
};
